import {
  Box,
  Center,
  Divider,
  Flex,
  Heading,
  IconButton,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { type ExtendedPeerInfo, type NetworkType } from "@tezos-x/octez.connect-wallet";
import { type ImplicitAddress, type Network, type Networks, type RawPkh } from "@umami/chains";
import {
  useAppSelector,
  useBeaconPeers,
  useDisconnectWalletConnectPeer,
  useGetBeaconConnectionInfo,
  useGetWcPeerListToggle,
  useRemoveBeaconPeer,
  walletKit,
} from "@umami/state";
import { type SessionTypes } from "@walletconnect/types";
import { getSdkError } from "@walletconnect/utils";
import capitalize from "lodash/capitalize";
import { useMemo } from "react";

import { CodeSandboxIcon, TrashIcon } from "../../../assets/icons";
import { useColor } from "../../../styles/useColor";
import { AddressPill } from "../../AddressPill";
import { EmptyMessage } from "../../EmptyMessage";

type ExtendedPeerSession = {
  id: string;
  name: string;
  icon: string;
  type: string;
  sessionInfo: SessionTypes.Struct;
  accountPkh: RawPkh;
  networkType: NetworkType;
} & ExtendedPeerInfo;

const getNetworkTypeFromWCSession = (namespaces: SessionTypes.Namespaces, networks: Networks) => {
  const networkNames: string[] = [];

  // Handle eip155 namespaces
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (namespaces.eip155?.chains) {
    // Flatten all networks from the networks object
    const allNetworks: Network[] = [];
    for (const chainNetworks of Object.values(networks)) {
      allNetworks.push(...chainNetworks.mainnet, ...chainNetworks.testnet);
    }

    // Iterate through each eip155 chain
    for (const chainFullId of namespaces.eip155.chains) {
      // Find matching network by namespace and identifier
      const matchingNetwork = allNetworks.find(
        network => `${network.namespace}:${network.identifier}` === chainFullId
      );

      if (matchingNetwork) {
        networkNames.push(matchingNetwork.name);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (namespaces.tezos?.chains) {
    networkNames.push(`Tezos ${namespaces.tezos.chains[0].split(":")[1]}`);
  }

  return networkNames.length > 0 ? networkNames.join(", ") : "Unknown";
};

export const GenericConnections = () => {
  // TODO: remove this once we have a proper way to subscribe to wc peer list updates
  const isUpdated = useGetWcPeerListToggle();
  const networks = useAppSelector(s => s.networks.networks);

  const { peers: beaconPeers } = useBeaconPeers();
  const { data: wcPeers } = useQuery({
    queryKey: ["wcPeers", isUpdated],
    queryFn: () => walletKit.getActiveSessions(),
    initialData: {},
  });
  const peers = useMemo(
    () =>
      [
        ...beaconPeers,
        ...Object.entries(wcPeers).map(([topic, session]) => ({
          id: topic,
          name: session.peer.metadata.name,
          icon: session.peer.metadata.icons[0],
          type: "wc",
          sessionInfo: session,
          accountPkh: session.self.publicKey,
          networkType: getNetworkTypeFromWCSession(session.namespaces, networks),
        })),
      ] as ExtendedPeerSession[],
    [beaconPeers, wcPeers, networks]
  );

  if (peers.length === 0) {
    return (
      <EmptyMessage
        alignItems="flex-start"
        marginTop="40px"
        data-testid="beacon-peers-empty"
        subtitle="No Apps to show"
        title="Your Apps will appear here..."
      />
    );
  }

  return (
    <VStack
      alignItems="flex-start"
      gap="24px"
      marginTop="24px"
      data-testid="beacon-peers"
      divider={<Divider />}
      spacing="0"
    >
      {peers.map(peerInfo => (
        <PeerRow key={peerInfo.id} peerInfo={peerInfo} />
      ))}
    </VStack>
  );
};

const PeerRow = ({ peerInfo }: { peerInfo: ExtendedPeerSession }) => {
  const color = useColor();
  const removePeer = useRemoveBeaconPeer();
  const disconnectWalletConnectPeer = useDisconnectWalletConnectPeer();

  const handleRemovePeer = () => {
    if (peerInfo.type === "wc") {
      return disconnectWalletConnectPeer({
        topic: peerInfo.sessionInfo.topic,
        reason: getSdkError("USER_DISCONNECTED"),
      });
    } else {
      return removePeer(peerInfo);
    }
  };

  return (
    <Center
      alignItems="center"
      justifyContent="space-between"
      width="full"
      height="60px"
      data-testid="peer-row"
    >
      <Flex width="100%" minWidth="0" height="100%">
        <Box
          width="60px"
          height="60px"
          marginRight="12px"
          borderRadius="50%"
          backgroundColor={color("100")}
        >
          <Center flex="0 0 auto" width="60px" height="100%">
            <Image
              borderRadius="50%"
              objectFit="cover"
              fallback={<CodeSandboxIcon width="36px" height="36px" />}
              src={peerInfo.icon}
            />
          </Center>
        </Box>
        <Center alignItems="flex-start" flexDirection="column" gap="6px" width="100%" minWidth="0">
          <Heading color={color("900")} size="lg">
            {peerInfo.name}
          </Heading>
          <StoredPeerInfo peerInfo={peerInfo} />
        </Center>
      </Flex>
      <IconButton
        color={color("500")}
        aria-label="Remove Peer"
        icon={<TrashIcon />}
        onClick={handleRemovePeer}
        variant="iconButtonSolid"
      />
    </Center>
  );
};

const StoredPeerInfo = ({ peerInfo }: { peerInfo: ExtendedPeerSession }) => {
  const beaconConnectionInfo = useGetBeaconConnectionInfo(peerInfo.senderId);
  const isWalletConnect = peerInfo.type === "wc";
  const connectionInfo = isWalletConnect ? peerInfo : beaconConnectionInfo;

  if (!connectionInfo) {
    return null;
  }

  const networkName = isWalletConnect
    ? peerInfo.networkType
    : capitalize(connectionInfo.networkType);

  return (
    <Flex width="100%" maxWidth="100%">
      <AddressPill
        minWidth="15%"
        marginRight="10px"
        address={{ type: "implicit", pkh: connectionInfo.accountPkh } as ImplicitAddress}
      />
      <Divider marginRight="10px" orientation="vertical" />
      <Text
        display={{ base: "none", md: "inline" }}
        marginTop="2px"
        marginRight="4px"
        fontWeight={600}
        size="sm"
      >
        Network:
      </Text>
      <Text
        overflow="hidden"
        minWidth="15%"
        marginTop="2px"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        data-testid="dapp-connection-network"
        size="sm"
      >
        {networkName}
      </Text>
    </Flex>
  );
};
